// Funciones puras para la edición masiva de inscritos: no tocan Firebase ni
// Redux, solo calculan "qué cambiaría" dado un inscrito y los parámetros de
// una operación, o resuelven etiquetas legibles de modalidad/categoría.
// Se mantienen separadas del componente para poder probarlas sin montar UI,
// mismo criterio que reportsLogic.jsx/inventoryLogic.js en este proyecto.

import { normalizarOrder } from "../../ParticipantDetail/orderDetailLogic";

const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };

// Campos que el sistema asigna internamente (pago, metadatos del formulario)
// y que no tiene sentido ofrecer como "campo origen" para renombrar.
const CAMPOS_INTERNOS_EXCLUIDOS = new Set([
  "monedaAUsar",
  "monedaBase",
  "order",
  "serverTime",
  "statusPago",
  "tipoRegistro",
  "usuarioRegistra",
  "versionForm",
  "mails",
  // no son atributos del inscrito: son estructurales o calculados en cliente
  "key",
  "changesRegistry",
]);

// Lista de atributos que realmente existen en los inscritos seleccionados,
// para poblar el selector de "Campo origen" al renombrar (en vez de texto
// libre, que invitaría a escribir un nombre que no existe en ninguno).
export function listarAtributosDisponibles(rows) {
  const encontrados = new Set();
  (rows || []).forEach((row) => {
    Object.keys(row || {}).forEach((k) => {
      if (k.indexOf("__") === 0) return; // labels calculados en el cliente
      if (CAMPOS_INTERNOS_EXCLUIDOS.has(k)) return;
      encontrados.add(k);
    });
  });
  return Array.from(encontrados).sort((a, b) => a.localeCompare(b));
}

export function resolverModalidadLabel(eventSubEventsRegistry, codMod) {
  if (!eventSubEventsRegistry || codMod === undefined || codMod === null || codMod === "") {
    return "";
  }
  const modalidad = eventSubEventsRegistry["modalidad_" + codMod];
  return (modalidad && modalidad.descripcionModalidad) || String(codMod);
}

export function resolverCategoriaLabel(eventSubEventsRegistry, codMod, rama, codCat) {
  if (!eventSubEventsRegistry || codMod === undefined || codMod === null || codMod === "") {
    return "";
  }
  const modalidad = eventSubEventsRegistry["modalidad_" + codMod];
  const categoria =
    modalidad &&
    modalidad.ramaCat &&
    modalidad.ramaCat["rama_" + rama] &&
    modalidad.ramaCat["rama_" + rama].categorias &&
    modalidad.ramaCat["rama_" + rama].categorias["categoria_" + codCat];
  return (categoria && categoria.nombreCategoria) || String(codCat ?? "");
}

export function listarModalidades(eventSubEventsRegistry) {
  if (!eventSubEventsRegistry) return [];
  return Object.keys(eventSubEventsRegistry)
    .filter((k) => k.indexOf("modalidad_") === 0)
    .map((k) => {
      const cod = k.replace("modalidad_", "");
      return {
        value: cod,
        label: eventSubEventsRegistry[k].descripcionModalidad || cod,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Categorías de una modalidad, aplanadas across ambas ramas (una modalidad
// tiene categorías separadas por rama_V/rama_F). Se etiqueta con la rama
// para que el admin elija sin ambigüedad a cuál se refiere.
export function listarCategoriasDeModalidad(eventSubEventsRegistry, codMod) {
  if (!eventSubEventsRegistry || codMod === undefined || codMod === null || codMod === "") {
    return [];
  }
  const modalidad = eventSubEventsRegistry["modalidad_" + codMod];
  if (!modalidad || !modalidad.ramaCat) return [];

  const out = [];
  Object.keys(modalidad.ramaCat).forEach((ramaKey) => {
    const rama = ramaKey.replace("rama_", "");
    const categorias = (modalidad.ramaCat[ramaKey] || {}).categorias || {};
    Object.keys(categorias).forEach((catKey) => {
      const cod = catKey.replace("categoria_", "");
      out.push({
        value: cod,
        rama,
        label: `${NOMBRE_RAMA[rama] || rama} - ${categorias[catKey].nombreCategoria || cod}`,
      });
    });
  });
  return out;
}

// ---------------------------------------------------------------------
// Constructores de cambio por tipo de operación. Cada uno recibe el
// inscrito crudo (objeto de Redux/RTDB) y los parámetros del formulario, y
// devuelve { cambia, updates, resumen, motivo }:
//   - cambia: si hay algo que escribir para este inscrito.
//   - updates: objeto { campo: nuevoValor, ... } relativo a la raíz del
//     inscrito (el llamador antepone la ruta RTDB completa).
//   - resumen: texto "antes -> después" para la preview y el audit log.
//   - motivo: por qué se omite, cuando cambia es false.
// ---------------------------------------------------------------------

function construirCambioRenombrar(inscrito, { campoOrigen, campoDestino }) {
  if (!campoOrigen || !campoDestino) return { cambia: false, motivo: "faltan campos" };
  const valor = inscrito[campoOrigen];
  if (valor === undefined || valor === null) {
    return { cambia: false, motivo: `no tiene "${campoOrigen}"` };
  }
  return {
    cambia: true,
    updates: { [campoDestino]: valor, [campoOrigen]: null },
    resumen: `${campoOrigen}="${valor}" → ${campoDestino}="${valor}"`,
  };
}

function construirCambioCategoria(inscrito, { nuevaCategoria, nuevaRama }) {
  if (nuevaCategoria === undefined || nuevaCategoria === null || nuevaCategoria === "") {
    return { cambia: false, motivo: "sin categoría nueva" };
  }
  const catActual = inscrito.categoria;
  const ramaActual = inscrito.rama;
  const mismaCategoria = String(catActual) === String(nuevaCategoria);
  const mismaRama = !nuevaRama || String(ramaActual) === String(nuevaRama);
  if (mismaCategoria && mismaRama) {
    return { cambia: false, motivo: "ya tiene esa categoría" };
  }
  const updates = { categoria: nuevaCategoria };
  if (nuevaRama) updates.rama = nuevaRama;
  return {
    cambia: true,
    updates,
    resumen: `categoria: ${catActual ?? "(vacío)"} → ${nuevaCategoria}${
      nuevaRama ? ` (rama ${ramaActual ?? "(vacío)"} → ${nuevaRama})` : ""
    }`,
  };
}

function construirCambioGenero(inscrito, { nuevoGenero }) {
  if (!nuevoGenero) return { cambia: false, motivo: "sin género nuevo" };
  if (inscrito.rama === nuevoGenero) {
    return { cambia: false, motivo: "ya tiene ese género" };
  }
  return {
    cambia: true,
    updates: { rama: nuevoGenero },
    resumen: `rama: ${inscrito.rama ?? "(vacío)"} → ${nuevoGenero}`,
  };
}

function construirCambioCampoLibre(inscrito, { campo, valor }) {
  if (!campo) return { cambia: false, motivo: "falta el nombre del campo" };
  const valorActual = inscrito[campo];
  if (String(valorActual ?? "") === String(valor ?? "")) {
    return { cambia: false, motivo: "ya tiene ese valor" };
  }
  return {
    cambia: true,
    updates: { [campo]: valor },
    resumen: `${campo}: ${valorActual ?? "(vacío)"} → ${valor}`,
  };
}

// Agrega un ítem de "código de descuento" a la orden de cada inscrito
// seleccionado, con la misma forma que genera CodePayment.jsx durante el
// registro normal (para que el dashboard de códigos de descuento y el
// detalle de orden lo reconozcan igual). Como RTDB no soporta "agregar al
// arreglo" como update parcial, el update siempre es el arreglo completo
// con el ítem nuevo al final.
function construirCambioAgregarCodigoDescuento(inscrito, { codigo, monto }) {
  if (!codigo) return { cambia: false, motivo: "falta el código" };
  const montoNumero = Number(monto);
  if (!montoNumero || montoNumero <= 0) {
    return { cambia: false, motivo: "el monto del descuento debe ser mayor a 0" };
  }

  const ordenActual = normalizarOrder(inscrito.order);
  const yaTieneCodigo = ordenActual.some(
    (item) =>
      item &&
      item.code === "códigoDescuento" &&
      item.extraData &&
      item.extraData.code === codigo
  );
  if (yaTieneCodigo) {
    return { cambia: false, motivo: `ya tiene el código "${codigo}" aplicado` };
  }

  const nuevoItem = {
    code: "códigoDescuento",
    name: "Código de descuento",
    description: "Código de descuento",
    quantity: 1,
    unit_price: -Math.abs(montoNumero),
    precioKey: "",
    extraData: { code: codigo },
  };

  return {
    cambia: true,
    updates: { order: [...ordenActual, nuevoItem] },
    resumen: `+ código de descuento "${codigo}" (-$${montoNumero.toFixed(2)})`,
  };
}

export const TIPOS_OPERACION = {
  renombrar: {
    label: "Renombrar atributo (conserva el valor)",
    construir: construirCambioRenombrar,
  },
  categoria: {
    label: "Cambiar categoría",
    construir: construirCambioCategoria,
  },
  genero: {
    label: "Cambiar género",
    construir: construirCambioGenero,
  },
  campoLibre: {
    label: "Asignar/editar un campo (valor fijo)",
    construir: construirCambioCampoLibre,
  },
  agregarCodigoDescuento: {
    label: "Agregar item a la orden (código de descuento)",
    construir: construirCambioAgregarCodigoDescuento,
  },
};

export function construirCambio(tipo, inscrito, parametros) {
  const operacion = TIPOS_OPERACION[tipo];
  if (!operacion) return { cambia: false, motivo: "sin tipo de operación" };
  return operacion.construir(inscrito, parametros || {});
}

export function validarParametrosOperacion(tipo, parametros) {
  const p = parametros || {};
  if (tipo === "renombrar") {
    if (!p.campoOrigen || !p.campoDestino) {
      return "Completa el campo origen y el campo destino";
    }
    if (p.campoOrigen === p.campoDestino) {
      return "El campo origen y destino no pueden ser el mismo";
    }
    return null;
  }
  if (tipo === "categoria") {
    if (!p.nuevaCategoria && p.nuevaCategoria !== 0) return "Selecciona la nueva categoría";
    return null;
  }
  if (tipo === "genero") {
    if (!p.nuevoGenero) return "Selecciona el nuevo género";
    return null;
  }
  if (tipo === "campoLibre") {
    if (!p.campo) return "Indica el nombre del campo";
    return null;
  }
  if (tipo === "agregarCodigoDescuento") {
    if (!p.codigo) return "Indica el código de descuento";
    if (!p.monto || Number(p.monto) <= 0) return "Indica el monto del descuento (mayor a 0)";
    return null;
  }
  return "Selecciona un tipo de operación";
}
